import { ImageHostName } from '../../types';
import { replaceImageHost } from '../../utils/common';
import { imageNotFound } from './productFrames.config';
import { IProductVariant } from './productInterfaces';

enum Variant {
  masterVariant = 'masterVariant',
  variant = 'variant',
  variantList = 'variants',
  allVariants = 'allVariants',
  notAvailable = 'NOT_AVAILABLE',
}

const productMappingObj: { [key: string]: string } = {
  masterVariant: 'variant',
  STYLE_NAME: 'name',
  NAME_FRIENDLY: 'nameFriendly',
  BRAND_CODE: 'brand',
  BRAND_FRIENDLY: 'brandFriendly',
  FRAME_GENDER_GROUP: 'genderGroup',
  FRAME_SHAPE_GROUP: 'shapeGroup',
  FRAME_TYPE_GROUP: 'typeGroup',
  FRAME_MATERIAL_GROUP: 'materialGroup',
  FRAME_SHAPE: 'shape',
  FRAME_TYPE: 'type',
  FRAME_MATERIAL: 'material',
  FRAME_GENDER: 'gender',
  centAmount: 'price',
  FRAME_SIZE: 'frameSize',
  SOURCE_CREATED_DATE: 'sourceCreated',
  RELEASE_DATE: 'releaseDate',
  MIN_PD: 'minPD',
  MAX_PD: 'maxPD',
  sku: 'sku',
  COLOR: 'color',
  IS_AVAILABLE: 'isAvailable',
  prices: 'price',
  VTO_AVAILABLE: 'isVTOAvailable',
  BRAND_LINK: 'brandLink',
  BRAND_NAME: 'brandName',
  SERVING_SIZE: 'size',
  NAME: 'name',
  CATEGORY: 'category',
  AGE_RANGE: 'ageRange',
  key: 'key',
  ITEM_FORM: 'itemForm',
  PRODUCT_BENEFITS: 'productBenefits',
  ACTIVE_INGREDIENTS: 'activeIngredients',
  TITLE: 'title',
  EYE_SIZE: 'eyeSize',
  DBL: 'dbl',
  MANUFACTURER: 'manufacturer',
  WEAR_SCHEDULE: 'wearability',
  TYPE: 'type',
  WEB_ENABLED: 'webEnabled',
};

// Define interfaces for better type safety
interface ISource {
  uri: string;
}

interface IImage {
  sources: ISource[];
}

interface IMedia {
  src: string;
  alt: string;
}

/**
 * Processes a list of images and returns an array of media objects with `src` and `alt` properties.
 * Ensures fallback values are used for invalid or missing image sources.
 *
 * @param images - Array of image objects to process.
 * @returns Array of media objects with validated `src` and `alt` properties.
 */
const getProductMedia = (images: IImage[]): IMedia[] => {
  const imagesList: IMedia[] = [];

  // Constants for fallback and default values
  const FALLBACK_SRC = 'https://images.framesnet.com/kp/Image.aspx';
  const FALLBACK_ALT = 'Image not found';
  const DEFAULT_ALT = 'Product Image';
  const IMAGE_HOST = 'images.framesnet.com';

  // Iterate over the list of images
  for (const imageObj of images) {
    // Initialize media object with fallback values
    const media: IMedia = {
      src: FALLBACK_SRC,
      alt: FALLBACK_ALT,
    };

    try {
      // Validate and process the image source
      if (Array.isArray(imageObj.sources) && imageObj.sources[0]?.uri?.trim()) {
        media.src = imageObj.sources[0].uri;
        media.alt = DEFAULT_ALT;

        // Append width parameter for specific host
        if (media.src.includes(IMAGE_HOST)) {
          media.src = `${media.src}&w=1000`;
        } else {
          const extension = media.src.split('.').pop()?.split('?')[0]?.toLowerCase();
          const format = extension === 'jpg' ? 'avif' : 'webp';
          const src = `${media.src}?format=${format}&quality=80`;
          media.src = replaceImageHost(src, ImageHostName.CONTENTSTACK, ImageHostName.KPSTORE);
        }
      } else {
        // Throw an error if the image source is invalid
        throw new Error('Invalid image source');
      }
    } catch (error: any) {
      // Log the error for debugging purposes
      console.error('Error processing image source:', error.message);
    }

    // Add the processed media object to the list
    imagesList.push(media);
  }
  return imagesList;
};

export const getProduct = (data: any, variant: 'variant' | 'masterVariant' | 'allVariants') => {
  const product = {} as IProductVariant;
  if (!data || Object.keys(data)?.length === 0) return product;
  const price = data.prices[0];
  const attributes = data.attributesRaw;
  if (price) {
    const { centAmount, fractionDigits } = price.value;
    if (centAmount > 0) {
      product.price = centAmount / 10 ** (fractionDigits > 2 ? 2 : fractionDigits || 2);
    } else {
      product.price = NaN;
    }
  }
  product.id = data.id;
  product.sku = data.sku;
  product.variant = variant;
  //  Get images of product
  product.media = getProductMedia(data.assets);
  //added vtoId in product for VTO widget
  data.attributesRaw?.forEach((attribute: any) => {
    const { value = '', name = '' } = attribute;
    if (name.includes('VTO_ID')) {
      product.vtoId = value;
      return true;
    }
  });

  if (product.media.length === 0) {
    const media = {
      src: imageNotFound,
      alt: 'image not found ',
    };
    product.media.push(media);
  }

  attributes.forEach((attr: any) => {
    if (attr) {
      const attributeMappedName: string = productMappingObj[attr.name];
      if (attributeMappedName) {
        let attributeValue = attr.value;
        if (attributeValue && attributeValue['label']) attributeValue = attributeValue.label;
        (product as any)[attributeMappedName] = attributeValue;
        if (attributeValue && attributeMappedName === 'webEnabled') {
          (product as any)[attributeMappedName] = attr.value.key;
        }
      }
    }
  });

  return product;
};

export const reshapeProductFrames = (data: any[], contentStackData?: any[], isPdp?: boolean) => {
  let productVariants = [];
  const products: Record<string, IProductVariant[]>[] = [];
  for (const productNumber in data) {
    const productObj = data[productNumber];
    productVariants = [];
    const productVariantsList = productObj[Variant.allVariants] || [];
    // other variants
    for (const variant of productVariantsList) {
      if (variant.length <= 0) break;
      let variantObj: any = getProduct(variant, Variant.allVariants);
      variantObj['key'] = productObj['key'];
      variantObj['categories'] = productObj['categories'];
      //remove comments when you want to show all variants on pdp regardless of color
      // if (!isPdp) {
      const isSameColorExist =
        productVariants.filter(
          itm => itm.color === variantObj.color && itm.name === variantObj.name,
        ).length > 0
          ? true
          : false;
      if (!isSameColorExist && variantObj?.color?.toUpperCase() != Variant.notAvailable) {
        productVariants.push(variantObj);
      }
      // } else {
      //   productVariants.push(variantObj);
      // }
    }

    productVariants = mergeContentStack([...productVariants], contentStackData);
    if (productVariants?.length > 0) {
      products.push({
        [productObj.id]: productVariants,
      });
    }
  }
  // TODO: Uncomment If we want to filter for the products everywhere in application where this function used
  // const resultedProducts = filterUndefinedPrcingProducts(products)
  return products;
};

export const filterUndefinedPrcingProducts = (products: Record<string, IProductVariant[]>[]) => {
  // Filtering for the $0 or null or undefined priced products
  const resultedProducts = products?.filter((product: Record<string, IProductVariant[]>) => {
    return !!Object.values(product)?.find((productVariants: IProductVariant[]) => {
      return !!productVariants?.find((variant: IProductVariant) => {
        return !!variant?.price && !Number.isNaN(variant.price);
      });
    });
  });
  console.warn(
    `Before filter products count ${products.length}, After: ${resultedProducts.length}`,
  );
  return resultedProducts;
};

const mergeContentStack = (data: any[], contentStack?: any[]) => {
  if (!contentStack) return data;
  for (let i = 0; i < contentStack.length; i++) {
    for (let j = 0; j < 1; j++) {
      if (
        (data[j] && data[j].key) === contentStack[i].productName &&
        contentStack[i]['defaultImages'].length
      ) {
        data[j]['media'][j]['src'] = contentStack[i]['defaultImages'][0].imageSrc;
      }
    }
  }
  return data;
};
